import { Search, ChevronDown } from "lucide-react";
import React, { useState } from "react";
import {
  Checkbox,
  TextInput,
  Stack,
  Text,
  ScrollArea,
  Paper,
  UnstyledButton,
  Loader,
  Group,
  createStyles,
} from "@mantine/core";
import { useClickOutside } from "@mantine/hooks";

/**
 * Minimal shape needed to list a clockable employee. Both
 * `EmployeeShiftRecord` (roster attendance) and `SiteEmployee` (the
 * company-wide replacement list) structurally satisfy this, so the picker can
 * mix scheduled records with searched-in company employees.
 */
export interface ClockCandidate {
  employee_id: number;
  employee_name: string;
  is_on_site?: boolean;
}

const useStyles = createStyles((theme) => ({
  trigger: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    border: `1px solid ${theme.colors.gray[4]}`,
    borderRadius: theme.radius.sm,
    padding: "7px 12px",
    fontSize: 14,
    cursor: "pointer",
    backgroundColor: theme.white,
    "&:hover": {
      borderColor: theme.colors.gray[5],
    },
  },
  dropdown: {
    position: "absolute",
    zIndex: 10,
    left: 0,
    right: 0,
    marginTop: 4,
  },
}));

interface EmployeeCheckboxListProps {
  employees: ClockCandidate[];
  selectedIds: number[];
  onChange: (selectedIds: number[]) => void;
  /** Controlled search term — lifted so the parent can fan a query out to the
   *  company-wide employee endpoint while typing. */
  search: string;
  onSearchChange: (value: string) => void;
  /** Spinner shown while the parent's company-wide search is in flight. */
  loading?: boolean;
  /** Message shown when no candidates match. */
  emptyLabel?: string;
}

const EmployeeCheckboxList: React.FC<EmployeeCheckboxListProps> = ({
  employees,
  selectedIds,
  onChange,
  search,
  onSearchChange,
  loading = false,
  emptyLabel = "No employees found",
}) => {
  const { classes } = useStyles();
  const [open, setOpen] = useState(false);
  const wrapperRef = useClickOutside(() => setOpen(false));

  // Client-side name filter on top of whatever the parent supplies. The parent
  // already injects company-wide matches for the current term, so this just
  // keeps the visible rows aligned with the box contents.
  const filtered = employees.filter((emp) =>
    emp.employee_name.toLowerCase().includes(search.toLowerCase())
  );

  const handleToggle = (employeeId: number): void => {
    if (selectedIds.includes(employeeId)) {
      onChange(selectedIds.filter((id) => id !== employeeId));
    } else {
      onChange([...selectedIds, employeeId]);
    }
  };

  const triggerLabel =
    selectedIds.length === 0
      ? "Employee Name"
      : `${selectedIds.length} employee${
          selectedIds.length > 1 ? "s" : ""
        } selected`;

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <Text size="sm" weight={500} mb={4}>
        Employee/s{" "}
        <Text component="span" color="red">
          *
        </Text>
      </Text>
      <UnstyledButton
        className={classes.trigger}
        onClick={() => setOpen(!open)}
        style={{ width: "100%" }}
      >
        <Text size="sm" color={selectedIds.length === 0 ? "dimmed" : undefined}>
          {triggerLabel}
        </Text>
        <ChevronDown size={16} color="#868e96" />
      </UnstyledButton>

      {open && (
        <Paper className={classes.dropdown} shadow="md" p="sm" withBorder>
          <Stack spacing="xs">
            <TextInput
              placeholder="Search"
              icon={<Search size={14} />}
              rightSection={loading ? <Loader size={14} /> : undefined}
              value={search}
              onChange={(e) => onSearchChange(e.currentTarget.value)}
              size="sm"
            />

            <ScrollArea style={{ maxHeight: 250 }}>
              <Stack spacing={6}>
                {filtered.length === 0 ? (
                  <Text size="sm" color="dimmed" align="center" py="sm">
                    {loading ? "Searching…" : emptyLabel}
                  </Text>
                ) : (
                  filtered.map((emp) => (
                    <Group key={emp.employee_id} spacing={6} noWrap>
                      <Checkbox
                        label={emp.employee_name}
                        checked={selectedIds.includes(emp.employee_id)}
                        onChange={() => handleToggle(emp.employee_id)}
                        size="sm"
                      />
                      {emp.is_on_site && (
                        <Text size="xs" color="#14926D">
                          • on site
                        </Text>
                      )}
                    </Group>
                  ))
                )}
              </Stack>
            </ScrollArea>
          </Stack>
        </Paper>
      )}
    </div>
  );
};

export default EmployeeCheckboxList;
